import React, { useRef, useEffect, useState, useCallback } from 'react';
import gsap from 'gsap';
import HeroCarousel3D from '../canvas/HeroCarousel3D';

/* -------------------------------------------------------
   Panel data — 0 = landing, 1-4 = services
   ------------------------------------------------------- */
const PANELS = [
  {
    type: 'landing',
    title: 'EURISTIK',
    tagline: 'Tus verticales, nuestras soluciones.',
  },
  {
    type: 'service',
    title: 'Inteligencia Artificial',
    tagline: 'Inteligencia que transforma datos en decisiones.',
  },
  {
    type: 'service',
    title: 'Logística',
    tagline: 'Cadenas optimizadas, operación continua.',
  },
  {
    type: 'service',
    title: 'Energía',
    tagline: 'Infraestructura crítica bajo control.',
  },
  {
    type: 'service',
    title: 'Seguridad Ciberfísica',
    tagline: 'Protección integrada, mundo ciberfísico.',
  },
];

export default function Hero({ heroSlide }) {
  const [activePanel, setActivePanel] = useState(0);

  const heroRef = useRef(null);
  const trackRef = useRef(null);
  const titleRef = useRef(null);
  const scrollIndicatorRef = useRef(null);
  const isTransitioningRef = useRef(false);
  const touchStartRef = useRef({ x: 0, y: 0 });

  // Shared transition state read by the 3D canvas each frame
  const transitionRef = useRef({
    fromPanel: 0,
    toPanel: 0,
    progress: 1,
    direction: 1,
  });

  // ---- Intro animation (EURISTIK letters + first tagline) ----
  useEffect(() => {
    const tl = gsap.timeline({ delay: 0.5 });

    if (titleRef.current) {
      const letters = titleRef.current.querySelectorAll('.letter');
      tl.fromTo(
        letters,
        { opacity: 0, y: 60, rotateX: -90 },
        {
          opacity: 1,
          y: 0,
          rotateX: 0,
          duration: 0.8,
          stagger: 0.05,
          ease: 'power3.out',
        }
      );
    }

    const firstTagline = document.querySelector('.hero-panel-tagline-0');
    if (firstTagline) {
      tl.fromTo(
        firstTagline,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, ease: 'power2.out' },
        '-=0.3'
      );
    }

    if (scrollIndicatorRef.current) {
      tl.fromTo(
        scrollIndicatorRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.6 },
        '-=0.2'
      );

      gsap.to(scrollIndicatorRef.current, {
        y: 10,
        duration: 1.5,
        ease: 'power1.inOut',
        yoyo: true,
        repeat: -1,
      });
    }

    return () => tl.kill();
  }, []);

  // ---- Navigate to panel ----
  const goToPanel = useCallback(
    (nextPanel, dir) => {
      if (isTransitioningRef.current) return;
      const clamped = Math.max(0, Math.min(nextPanel, PANELS.length - 1));
      if (clamped === activePanel) return;

      isTransitioningRef.current = true;

      // Update transition ref for 3D canvas
      transitionRef.current.fromPanel = activePanel;
      transitionRef.current.toPanel = clamped;
      transitionRef.current.progress = 0;
      transitionRef.current.direction = dir;

      // Animate 3D transition progress
      gsap.to(transitionRef.current, {
        progress: 1,
        duration: 1.0,
        ease: 'power3.inOut',
      });

      // Slide the track horizontally (each panel = 20% of track width)
      gsap.to(trackRef.current, {
        xPercent: -clamped * 20,
        duration: 1.0,
        ease: 'power3.inOut',
        onComplete: () => {
          isTransitioningRef.current = false;
        },
      });

      // Move the terrain camera laterally (read by ParticleNetwork)
      if (heroSlide) {
        gsap.to(heroSlide, {
          current: clamped,
          duration: 1.0,
          ease: 'power3.inOut',
        });
      }

      setActivePanel(clamped);
    },
    [activePanel, heroSlide]
  );

  const goNext = useCallback(() => {
    goToPanel(activePanel + 1, 1);
  }, [activePanel, goToPanel]);

  const goPrev = useCallback(() => {
    goToPanel(activePanel - 1, -1);
  }, [activePanel, goToPanel]);

  // ---- Keyboard: ArrowLeft / ArrowRight ----
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.target.closest('input, textarea, select')) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goNext, goPrev]);

  // ---- Touch swipe on hero section ----
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const onTouchStart = (e) => {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    };

    const onTouchEnd = (e) => {
      const dx = touchStartRef.current.x - e.changedTouches[0].clientX;
      const dy = touchStartRef.current.y - e.changedTouches[0].clientY;

      // Only handle horizontal swipes (>50px, and more horizontal than vertical)
      if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;

      if (dx > 0) {
        goNext();
      } else {
        goPrev();
      }
    };

    hero.addEventListener('touchstart', onTouchStart, { passive: true });
    hero.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      hero.removeEventListener('touchstart', onTouchStart);
      hero.removeEventListener('touchend', onTouchEnd);
    };
  }, [goNext, goPrev]);

  return (
    <section
      id="hero"
      ref={heroRef}
      className="section min-h-screen relative overflow-hidden"
    >
      {/* 3D Canvas — behind everything, visible on service panels */}
      <HeroCarousel3D activePanel={activePanel} transitionRef={transitionRef} />

      {/* Horizontal sliding track (5 panels, each 100vw) */}
      <div ref={trackRef} className="hero-panels-track">
        {/* Panel 0: EURISTIK landing */}
        <div className="hero-panel">
          <div className="flex flex-col items-center justify-center text-center">
            <h1 ref={titleRef} className="mb-6">
              <img
                src="/logos/euristik-logo.png"
                alt="EURISTIK"
                className="letter w-[280px] sm:w-[360px] md:w-[460px] lg:w-[560px] h-auto"
                style={{ filter: 'invert(1)' }}
              />
            </h1>
            <p className="hero-panel-tagline-0 text-lg sm:text-xl md:text-2xl text-white/40 font-body font-extralight max-w-2xl leading-relaxed tracking-wide">
              {PANELS[0].tagline}
            </p>
          </div>
        </div>

        {/* Panels 1-4: Services (alternating layout) */}
        {PANELS.slice(1).map((panel, i) => {
          // i=0,2 → 3D left / text right | i=1,3 → text left / 3D right
          const textOnRight = i % 2 === 0;
          return (
            <div key={i} className="hero-panel">
              <div
                className={`w-full h-full flex items-center justify-start ${
                  textOnRight ? 'md:justify-end' : 'md:justify-start'
                } px-8 md:px-16 lg:px-24`}
              >
                <div
                  className={`max-w-lg text-left ${
                    textOnRight ? 'md:text-left' : 'md:text-right'
                  }`}
                >
                  <h2 className="font-display font-medium text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-[0.08em] mb-4 text-white">
                    {panel.title}
                  </h2>
                  <p className="text-base sm:text-lg md:text-xl text-white/40 font-body font-extralight leading-relaxed tracking-wide">
                    {panel.tagline}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Arrow — Left */}
      <button
        className={`hero-arrow hero-arrow--left ${activePanel === 0 ? 'hero-arrow--hidden' : ''}`}
        onClick={goPrev}
        aria-label="Panel anterior"
        tabIndex={activePanel === 0 ? -1 : 0}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <polyline points="15,4 7,12 15,20" />
        </svg>
      </button>

      {/* Arrow — Right */}
      <button
        className={`hero-arrow hero-arrow--right ${activePanel === PANELS.length - 1 ? 'hero-arrow--hidden' : ''}`}
        onClick={goNext}
        aria-label="Siguiente panel"
        tabIndex={activePanel === PANELS.length - 1 ? -1 : 0}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <polyline points="9,4 17,12 9,20" />
        </svg>
      </button>

      {/* Dot indicators */}
      <div className="hero-dots">
        {PANELS.map((_, i) => (
          <button
            key={i}
            className={`hero-dot ${i === activePanel ? 'hero-dot--active' : ''}`}
            onClick={() => {
              const dir = i > activePanel ? 1 : -1;
              goToPanel(i, dir);
            }}
            aria-label={`Ir al panel ${i + 1}`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div
        ref={scrollIndicatorRef}
        className="absolute bottom-4 hidden md:flex flex-col items-center gap-2 z-10"
      >
        <span className="text-xs text-white/20 font-body font-extralight uppercase tracking-[0.3em]">
          Scroll
        </span>
        <div className="w-5 h-8 rounded-full border border-white/15 flex items-start justify-center p-1">
          <div className="w-1 h-2 rounded-full bg-white/30" />
        </div>
      </div>
    </section>
  );
}
